import React, { useRef, useState } from 'react';
import { computeTooltipPlacement } from './tooltipPositioning';

/**
 * Slider row used by compact calculator engagers (NPS vs UPS, corpus tools).
 * Styling lives in calculators.css (nps-ups-* classes).
 *
 * variant:
 * - `standard` — narrow readout + range slider between − / +
 * - `inr` — no slider; editable amount between − / + (same step / clamp logic)
 */
function snapToStep(raw, min, max, step) {
  const s = Number(step) || 1;
  if (s <= 0) return Math.min(max, Math.max(min, raw));
  const snapped = min + Math.round((raw - min) / s) * s;
  const rounded = Number(Number(snapped).toFixed(8));
  return Math.min(max, Math.max(min, rounded));
}

/** Parse typed readout: plain number, % suffix, or ₹ / grouping commas (en-IN). */
function parseReadoutText(text) {
  let t = String(text).trim().replace(/\s/g, '');
  if (!t) return null;
  t = t.replace(/₹/g, '').replace(/,/g, '');
  if (t.endsWith('%')) {
    t = t.slice(0, -1).trim();
  }
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return n;
}

export default function EngagerSliderRow({
  id,
  label,
  tooltip,
  value,
  setValue,
  min,
  max,
  format,
  step = 1,
  variant = 'standard',
  onLiveValueChange,
  disabled = false,
  labelAria,
  isDefaultValue = false,
  defaultValue,
}) {
  const s = Number(step) || 1;
  const clampRaw = (n) => Math.min(max, Math.max(min, n));
  const clampStepped = (n) => snapToStep(n, min, max, s);
  const display = format(value);
  const tooltipId = `${id}-tip`;
  const readoutId = `${id}-readout`;
  const readoutRef = useRef(null);
  const tooltipWrapRef = useRef(null);
  const [tooltipPosClass, setTooltipPosClass] = useState('');
  const [tooltipInlineStyle, setTooltipInlineStyle] = useState(undefined);
  const [readoutFocused, setReadoutFocused] = useState(false);
  const [readoutDraft, setReadoutDraft] = useState(null);

  const isInr = variant === 'inr';
  const labelAriaText =
    labelAria ||
    (typeof label === 'string' ? label : String(id || 'value'));
  const clampForTyped = (n) => (isInr ? clampRaw(n) : clampStepped(n));
  const showPctCompact =
    !isInr &&
    (String(format(Number(max))).includes('%') ||
      String(format(Number(min))).includes('%'));

  const matchesDefault =
    defaultValue !== undefined &&
    defaultValue !== null &&
    Number.isFinite(Number(value)) &&
    Number.isFinite(Number(defaultValue)) &&
    Math.abs(Number(value) - Number(defaultValue)) < 1e-9;
  const showDefaultTone =
    (isDefaultValue || matchesDefault) && !readoutFocused && !disabled;

  const readoutLeftClassName = [
    'nps-ups-slider-readout-left',
    showPctCompact
      ? 'nps-ups-slider-readout--compact-pct'
      : 'nps-ups-slider-readout--compact-int',
    showDefaultTone ? 'nps-ups-slider-readout--default' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const readoutInlineClassName = [
    'nps-ups-slider-readout-inline',
    showDefaultTone ? 'nps-ups-slider-readout--default' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const updateTooltipPosition = () => {
    const placement = computeTooltipPlacement(tooltipWrapRef.current);
    setTooltipPosClass(placement.tooltipPosClass);
    setTooltipInlineStyle(placement.tooltipInlineStyle);
  };

  const getCommittedNumeric = () => {
    if (readoutFocused && readoutDraft != null && readoutDraft.trim() !== '') {
      const p = parseReadoutText(readoutDraft);
      if (p !== null) return clampForTyped(p);
    }
    return clampForTyped(Number(value));
  };

  const emitLiveValue = (rawTextOrNumber) => {
    if (!onLiveValueChange) return;
    if (typeof rawTextOrNumber === 'number') {
      onLiveValueChange(clampForTyped(rawTextOrNumber));
      return;
    }
    const text = String(rawTextOrNumber ?? '').trim();
    if (!text) {
      onLiveValueChange(null);
      return;
    }
    const parsed = parseReadoutText(text);
    onLiveValueChange(parsed !== null ? clampForTyped(parsed) : null);
  };

  const onSlider = (e) => {
    if (disabled) return;
    setReadoutDraft(null);
    setReadoutFocused(false);
    const next = clampStepped(Number(e.target.value));
    setValue(next);
    emitLiveValue(next);
  };

  const dec = () => {
    if (disabled) return;
    const v0 = getCommittedNumeric();
    setReadoutDraft(null);
    setReadoutFocused(false);
    const next = clampRaw(v0 - s);
    setValue(next);
    emitLiveValue(next);
  };

  const inc = () => {
    if (disabled) return;
    const v0 = getCommittedNumeric();
    setReadoutDraft(null);
    setReadoutFocused(false);
    const next = clampRaw(v0 + s);
    setValue(next);
    emitLiveValue(next);
  };

  const onReadoutFocus = () => {
    if (disabled) return;
    setReadoutFocused(true);
    setReadoutDraft(format(value));
  };

  const onReadoutBlur = () => {
    if (disabled) return;
    setReadoutFocused(false);
    if (readoutDraft != null && readoutDraft.trim() !== '') {
      const p = parseReadoutText(readoutDraft);
      if (p !== null) {
        const next = clampForTyped(p);
        setValue(next);
        emitLiveValue(next);
      } else {
        emitLiveValue(Number(value));
      }
    } else {
      emitLiveValue(Number(value));
    }
    setReadoutDraft(null);
  };

  const onReadoutChange = (e) => {
    if (disabled) return;
    setReadoutDraft(e.target.value);
    emitLiveValue(e.target.value);
  };

  const onReadoutKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      readoutRef.current?.blur();
    }
  };

  const blurReadoutForRange = () => {
    readoutRef.current?.blur();
  };

  const readoutShown =
    readoutFocused && readoutDraft !== null ? readoutDraft : display;

  const mn = Number(min);
  const mx = Number(max);
  const fillPct =
    !Number.isFinite(mn) || !Number.isFinite(mx) || mx === mn
      ? 0
      : Math.min(
          100,
          Math.max(0, ((Number(value) - mn) / (mx - mn)) * 100),
        );

  const readoutInput = (
    <input
      ref={readoutRef}
      id={readoutId}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={isInr ? readoutInlineClassName : readoutLeftClassName}
      value={readoutShown}
      onChange={onReadoutChange}
      onFocus={onReadoutFocus}
      onBlur={onReadoutBlur}
      onKeyDown={onReadoutKeyDown}
      disabled={disabled}
      readOnly={disabled}
      aria-label={`${labelAriaText} value`}
      title={disabled ? undefined : 'Type a value or use − / +'}
    />
  );

  return (
    <div
      className={`border-bottom-light nps-ups-step-row${
        disabled ? ' nps-ups-step-row--disabled' : ''
      }`}
    >
      <div className="grid-2-col-2-1 nps-ups-slider-grid">
        <div className="nps-ups-field-label">
          <span className="nps-ups-step-label">{label}</span>
          {tooltip ? (
            <span
              ref={tooltipWrapRef}
              className={`nps-ups-tooltip-wrap ${tooltipPosClass}`.trim()}
            >
              <button
                type="button"
                className="btn-small-round nps-ups-q"
                aria-describedby={tooltipId}
                aria-label={`Tip for ${labelAriaText}`}
                onMouseEnter={updateTooltipPosition}
                onFocus={updateTooltipPosition}
              >
                ?
              </button>
              <span
                className="nps-ups-tooltip-bubble"
                id={tooltipId}
                role="tooltip"
                style={tooltipInlineStyle}
              >
                {tooltip}
              </span>
            </span>
          ) : null}
        </div>
        <div
          className={`form-field-stack nps-ups-slider-col${
            isInr ? ' nps-ups-slider-col--inr-only' : ''
          }`.trim()}
        >
          {!isInr ? readoutInput : null}
          <div
            className={`nps-ups-slider-rail${
              isInr ? ' nps-ups-slider-rail--inr' : ''
            }`.trim()}
          >
            <button
              type="button"
              className="btn-small-round"
              onClick={dec}
              disabled={disabled}
              aria-disabled={disabled}
            >
              −
            </button>
            {isInr ? (
              readoutInput
            ) : (
              <input
                id={id}
                type="range"
                min={min}
                max={max}
                step={s}
                value={value}
                onChange={onSlider}
                onMouseDown={blurReadoutForRange}
                onTouchStart={blurReadoutForRange}
                onFocus={blurReadoutForRange}
                disabled={disabled}
                aria-label={`${labelAriaText}: ${display}`}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={value}
                aria-valuetext={display}
                style={
                  {
                    '--engager-range-fill': `${fillPct}%`,
                  }
                }
              />
            )}
            <button
              type="button"
              className="btn-small-round"
              onClick={inc}
              disabled={disabled}
              aria-disabled={disabled}
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
